import { useMemo } from 'react';

import { useSetRecoilState } from 'recoil';

import type { UserPreferenceLocation } from '@cognite/apm-client';

import { useCurrentUserContext } from '../../../providers/current-user-provider/current-user-provider';
import { useAppConfigContext } from '../../../providers/is-idm-provider/app-config-provider';
import { useUpsertCurrentUserPreferences } from '../../user/hooks/use-mutation/use-upsert-current-user-preferences';
import { selectedRootLocationAtom } from '../state/state';
import {
  getIsNewConfigVersion,
  SELECTED_ROOT_LOCATION_KEY,
} from '../utils/utils';

import { useSelectableLocations } from './use-selectable-locations';
import { getSelectableIdmLocationOptions } from './utils/get-selectable-idm-location-options';
import { getSelectableLocationOptions } from './utils/get-selectable-location-options';

export interface LocationOption {
  label: string;
  value: UserPreferenceLocation;
}

export interface UseLocationsReturnInterface {
  selectableLocations: UserPreferenceLocation[] | undefined;
  isSelectableLocationsLoading: boolean;
  configuredRootLocationOptions: LocationOption[];
  selectedRootLocation: LocationOption | undefined;
  handleSaveLocation: (location?: UserPreferenceLocation) => Promise<void>;
  updateLocationInLocalStorage: (location?: UserPreferenceLocation) => void;
}

export const useLocations = (): UseLocationsReturnInterface => {
  const { appConfig, isIdm, idmLocations } = useAppConfigContext();
  const {
    data: selectableNonIdmLocations,
    isLoading: isSelectableLocationsLoading,
  } = useSelectableLocations();
  const selectableIdmLocations = getSelectableIdmLocationOptions(idmLocations);

  const selectableLocations = isIdm
    ? selectableIdmLocations
    : selectableNonIdmLocations;

  const { user: userData } = useCurrentUserContext();

  const { mutateAsync: upsertUserPreferences } =
    useUpsertCurrentUserPreferences();

  const isNewConfigVersion = getIsNewConfigVersion(appConfig);

  const configuredRootLocationOptions = useMemo(() => {
    return getSelectableLocationOptions(
      isIdm,
      selectableLocations,
      appConfig,
      isNewConfigVersion
    );
  }, [appConfig, isIdm, isNewConfigVersion, selectableLocations]);

  const userLocationPreference = userData?.preferences?.infield?.location;

  const selectedRootLocation = configuredRootLocationOptions.find(
    ({ value }) =>
      value.externalId === userLocationPreference?.externalId ||
      value.externalId === userLocationPreference?.assetExternalId
  );

  const setSelectedRootLocation = useSetRecoilState(selectedRootLocationAtom);

  const updateLocationInLocalStorage = (location?: UserPreferenceLocation) => {
    const locationAssetExternalId = isNewConfigVersion
      ? location?.externalId
      : location?.assetExternalId || location?.externalId;
    if (!locationAssetExternalId) {
      localStorage.removeItem(SELECTED_ROOT_LOCATION_KEY);
      return;
    }
    localStorage.setItem(SELECTED_ROOT_LOCATION_KEY, locationAssetExternalId);
  };

  const handleSaveLocation = async (location?: UserPreferenceLocation) => {
    await upsertUserPreferences(
      {
        preference: 'location',
        data: location,
      },
      {
        onSuccess: () => {
          updateLocationInLocalStorage(location);
          setSelectedRootLocation(location);
        },
      }
    );
  };

  return {
    selectableLocations,
    isSelectableLocationsLoading,
    configuredRootLocationOptions,
    selectedRootLocation,
    handleSaveLocation,
    updateLocationInLocalStorage,
  };
};
